"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Link from "next/link";

const loginSchema = z.object({
  email: z.string().email({ message: "Ingresa un correo válido" }),
  password: z.string().min(6, { message: "La contraseña debe tener al menos 6 caracteres" }),
  remember: z.boolean().optional(),
});

type LoginSchema = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSubmit?: (values: { email: string; password: string; remember?: boolean }) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export function LoginForm({ onSubmit: externalSubmit, isLoading: externalLoading, error: externalError }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      remember: true,
    },
  });

  // Resetear estado de loading cuando el componente se monte
  useEffect(() => {
    setIsLoggingIn(false);
  }, []);

  const loading = externalLoading ?? isLoggingIn;

  const onSubmit = async (values: LoginSchema) => {
    if (externalSubmit) {
      await externalSubmit(values);
      return;
    }

    // Fallback: comportamiento original (login directo sin orquestador)
    setIsLoggingIn(true);
    try {
      const { login } = await import("@/modules/auth/api");
      await login({
        email: values.email,
        password: values.password,
        remember: values.remember,
      });

      setIsLoggingIn(false);

      if (typeof window !== "undefined") {
        const currentPath = window.location.pathname;
        if (currentPath.startsWith("/app/")) {
          window.location.href = "/app/dashboard";
        } else {
          window.location.href = "/dashboard";
        }
      }
    } catch {
      const { toast } = await import("sonner");
      toast.error("No pudimos iniciar sesión. Verifica tus credenciales o consulta al administrador.");
      setIsLoggingIn(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {externalError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {externalError}
        </div>
      )}

      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground" htmlFor="email">
          Correo electrónico
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            placeholder="usuario@empresa.com"
            className={`pl-10 h-9 ${errors.email ? 'border-red-500' : ''}`}
            {...register("email")}
          />
        </div>
        {errors.email ? (
          <p className="text-xs text-red-500">{errors.email.message}</p>
        ) : (
          <p className="text-xs text-muted-foreground">Usa el correo corporativo asignado por tu empresa.</p>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground" htmlFor="password">
          Contraseña
        </label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="Ingresa tu contraseña"
            className={`pr-10 h-9 ${errors.password ? 'border-red-500' : ''}`}
            {...register("password")}
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.password ? (
          <p className="text-xs text-red-500">{errors.password.message}</p>
        ) : (
          <p className="text-xs text-muted-foreground">Si olvidaste tu contraseña, puedes restablecerla desde el enlace de abajo.</p>
        )}
      </div>

      <div className="flex items-center justify-between text-xs">
        <label className="inline-flex items-center gap-2 text-muted-foreground">
          <input type="checkbox" className="h-3 w-3 rounded border-border" {...register("remember")} />
          Recordarme
        </label>
        <Link href="/forgot-password" className="text-primary hover:underline">
          ¿Olvidaste tu contraseña?
        </Link>
      </div>

      <Button type="submit" className="w-full h-9" disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ingresar"}
      </Button>

      {loading && (
        <p className="text-center text-xs text-muted-foreground animate-pulse">
          Verificando Acceso...
        </p>
      )}

      <p className="text-center text-xs text-muted-foreground">
        ¿Necesitas acceso? Contacta al administrador de tu empresa.
      </p>
    </form>
  );
}
